import type { DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import {
  DEFAULT_PHRASE_SORTING,
  PHRASE_COLUMNS,
  PHRASES_PAGE_SIZE,
  PHRASES_PAGE_SIZE_OPTIONS,
  SORTABLE_PHRASE_COLUMNS,
} from './constants';
import type { AudioAuditState, NumberRange, TimestampRange } from './types';

/**
 * Имена query-параметров раздела: окно, отбор и порядок перечня живут в адресе, поэтому возврат
 * из карточки диалога реплики приводит в аудит с тем же перечнем. Контракт задаётся здесь один
 * раз — и разбором адреса, и его сборкой.
 */
const QUERY_KEYS = {
  page: 'page',
  size: 'size',
  sort: 'sort',
  conversationIdFrom: 'conversation-id-from',
  conversationIdTo: 'conversation-id-to',
  durationFrom: 'duration-from',
  durationTo: 'duration-to',
  updatedAtFrom: 'updated-at-from',
  updatedAtTo: 'updated-at-to',
} as const;

/** Снятый пользователем порядок: от отсутствующего параметра он отличается — тот означает порядок по умолчанию */
const NO_SORTING = 'none';

const parseNumber = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Страница в адресе считается от единицы — так её читает пользователь */
const parsePage = (value: string | null): number => {
  const parsed = parseNumber(value);
  return parsed === undefined || parsed < 1 ? 0 : Math.floor(parsed) - 1;
};

const parsePageSize = (value: string | null): number => {
  const parsed = parseNumber(value);
  return parsed !== undefined && PHRASES_PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : PHRASES_PAGE_SIZE;
};

const parseSorting = (value: string | null): DataGridSortingState => {
  if (value === NO_SORTING) return [];
  if (!value) return DEFAULT_PHRASE_SORTING;

  const [columnId, direction] = value.split(':');
  if (!SORTABLE_PHRASE_COLUMNS[columnId] || (direction !== 'asc' && direction !== 'desc'))
    return DEFAULT_PHRASE_SORTING;

  return [{ columnId, direction }];
};

/** Границы отбора: пустые с обеих сторон в отборе не участвуют */
const toRange = <T extends NumberRange | TimestampRange>(range: T): T | undefined =>
  range.from === undefined && range.to === undefined ? undefined : range;

const parseFilters = (params: URLSearchParams): DataGridFilterState => {
  const conversationId = toRange<NumberRange>({
    from: parseNumber(params.get(QUERY_KEYS.conversationIdFrom)),
    to: parseNumber(params.get(QUERY_KEYS.conversationIdTo)),
  });
  const duration = toRange<NumberRange>({
    from: parseNumber(params.get(QUERY_KEYS.durationFrom)),
    to: parseNumber(params.get(QUERY_KEYS.durationTo)),
  });
  const updatedAt = toRange<TimestampRange>({
    from: params.get(QUERY_KEYS.updatedAtFrom) ?? undefined,
    to: params.get(QUERY_KEYS.updatedAtTo) ?? undefined,
  });

  return {
    ...(conversationId ? { [PHRASE_COLUMNS.conversationId]: { value: conversationId } } : {}),
    ...(duration ? { [PHRASE_COLUMNS.duration]: { value: duration } } : {}),
    ...(updatedAt ? { [PHRASE_COLUMNS.updatedAt]: { value: updatedAt } } : {}),
  };
};

/** Читает окно, отбор и порядок перечня из адреса раздела; неизвестное значение берётся по умолчанию */
export const parseAudioAuditQuery = (params: URLSearchParams): AudioAuditState => ({
  page: parsePage(params.get(QUERY_KEYS.page)),
  pageSize: parsePageSize(params.get(QUERY_KEYS.size)),
  sorting: parseSorting(params.get(QUERY_KEYS.sort)),
  filters: parseFilters(params),
});

const setParam = (params: URLSearchParams, key: string, value: string | null) => {
  if (value === null) params.delete(key);
  else params.set(key, value);
};

const toParamValue = (value: number | string | undefined): string | null =>
  value === undefined || value === '' ? null : String(value);

const toSortingParam = (sorting: DataGridSortingState): string | null => {
  const [activeSort] = sorting;
  if (!activeSort) return NO_SORTING;

  const [defaultSort] = DEFAULT_PHRASE_SORTING;
  if (activeSort.columnId === defaultSort.columnId && activeSort.direction === defaultSort.direction) return null;

  return `${activeSort.columnId}:${activeSort.direction}`;
};

/**
 * Переносит окно, отбор и порядок перечня в адрес раздела. Значения по умолчанию из адреса
 * убираются — адрес несёт только то, что пользователь выбрал сам. Чужие параметры сохраняются.
 */
export const writeAudioAuditQuery = (prev: URLSearchParams, state: AudioAuditState): URLSearchParams => {
  const next = new URLSearchParams(prev);
  const conversationId = state.filters[PHRASE_COLUMNS.conversationId]?.value as NumberRange | undefined;
  const duration = state.filters[PHRASE_COLUMNS.duration]?.value as NumberRange | undefined;
  const updatedAt = state.filters[PHRASE_COLUMNS.updatedAt]?.value as TimestampRange | undefined;

  setParam(next, QUERY_KEYS.page, state.page > 0 ? String(state.page + 1) : null);
  setParam(next, QUERY_KEYS.size, state.pageSize === PHRASES_PAGE_SIZE ? null : String(state.pageSize));
  setParam(next, QUERY_KEYS.sort, toSortingParam(state.sorting));
  setParam(next, QUERY_KEYS.conversationIdFrom, toParamValue(conversationId?.from));
  setParam(next, QUERY_KEYS.conversationIdTo, toParamValue(conversationId?.to));
  setParam(next, QUERY_KEYS.durationFrom, toParamValue(duration?.from));
  setParam(next, QUERY_KEYS.durationTo, toParamValue(duration?.to));
  setParam(next, QUERY_KEYS.updatedAtFrom, toParamValue(updatedAt?.from));
  setParam(next, QUERY_KEYS.updatedAtTo, toParamValue(updatedAt?.to));

  return next;
};
